import { useNavigate, Link } from 'react-router-dom'
import {
  Menu,
  Search,
  Bell,
  LogOut,
} from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { useUiStore } from '@/stores/uiStore'
import { useAdminPush } from '@/contexts/AdminPushContext'
import toast from 'react-hot-toast'
import { LOGO_ICON_PATH, LOGO_ALT } from '@/lib/brand'

export function Navbar() {
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()
  const {
    toggleSidebar,
    globalSearch,
    setGlobalSearch,
  } = useUiStore()
  const {
    enabled: pushEnabled,
    loading: pushLoading,
    enablePush,
    isConfigured,
    canEnablePush,
    platform,
  } = useAdminPush()

  const handleLogout = async () => {
    await logout()
    toast.success('Logged out successfully')
    navigate('/admin/login')
  }

  return (
    <header className="app-bar sticky top-0 z-30 glass-panel border-b border-luxury-700/50">
      <div className="app-bar-row justify-between gap-4 px-4 lg:px-6">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <button
            onClick={toggleSidebar}
            className="rounded-xl p-2 text-zinc-600 hover:bg-luxury-800 lg:hidden"
          >
            <Menu className="h-5 w-5" />
          </button>

          <div className="relative hidden sm:block flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
            <input
              type="search"
              placeholder="Search products, orders, customers..."
              value={globalSearch}
              onChange={(e) => setGlobalSearch(e.target.value)}
              className="input-luxury pl-10 py-2"
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            title={
              pushEnabled
                ? 'Order alerts enabled'
                : canEnablePush
                  ? 'Enable order push alerts'
                  : platform.reason
                    ? 'See order alert instructions on dashboard'
                    : isConfigured
                      ? 'Push not available in this browser'
                      : 'Push not configured (VAPID key)'
            }
            disabled={pushLoading || pushEnabled}
            onClick={() => void enablePush()}
            className={`relative rounded-xl p-2 hover:bg-luxury-800 ${
              pushEnabled ? 'text-gold-600' : 'text-zinc-600'
            } disabled:opacity-60`}
          >
            <Bell className="h-5 w-5" />
            {pushEnabled && (
              <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-emerald-500" />
            )}
          </button>

          <div className="hidden sm:flex items-center gap-3 pl-3 border-l border-luxury-700">
            <div className="text-right">
              <p className="text-sm font-medium text-zinc-800 truncate max-w-[140px]">
                {user?.email || 'Admin'}
              </p>
              <p className="text-[10px] text-gold-600/80 uppercase tracking-wider">
                Administrator
              </p>
            </div>
            <Link
              to="/"
              aria-label="Ooudh Kraft home"
              className="h-9 w-9 rounded-full overflow-hidden ring-2 ring-gold-400/40 bg-white transition-opacity hover:opacity-90"
            >
              <img
                src={LOGO_ICON_PATH}
                alt={LOGO_ALT}
                className="h-full w-full object-contain"
              />
            </Link>
          </div>

          <button
            onClick={handleLogout}
            className="rounded-xl p-2 text-zinc-600 hover:bg-red-500/10 hover:text-red-500"
            title="Logout"
          >
            <LogOut className="h-5 w-5" />
          </button>
        </div>
      </div>
    </header>
  )
}
